/**
 * Highlights reel — full-screen, program-style playback of the recorded clips,
 * cut exactly the way the player cut them live.
 *
 *   intro card → [stinger] clip 1 (player's own cuts, lower third, verdict stamp)
 *              → [stinger] REPLAY slow-mo from the best angle (goals)
 *              → … → [stinger] outro card (grade + "Directed by you")
 *
 * The reel owns its own World + CameraCrew + Multiview on the shared renderer (the
 * match session is gone by the time it runs). Live segments feed the recorded
 * snapshots through the live operators, so cameras behave like they did on the night;
 * on-air replays and the slow-mo pass use the crew's replay operators.
 *
 * Save video: renderer → 1280×720, each frame is drawImage()d into a 2D canvas, the
 * essential overlays are painted by ./compositor (DOM isn't captured), and
 * captureStream() of that canvas (+ the game audio, if running) goes to MediaRecorder.
 */
import './reel.css';
import * as THREE from 'three';
import type {
  CutRecord, DirectorState, GraphicInstance, MatchEvent, MatchEventType, MatchStats, ReplayState, ResultSummary, TeamMatchStats, WorldSnapshot,
} from '../core/contracts';
import { createWorld, type World, type WorldQuality } from '../render/world';
import { createCameraCrew, type CameraCrew, type FeedSource } from '../render/cameras/operators';
import { createMultiview, type FeedLayout, type Multiview } from '../render/cameras/multiview';
import { createEmptySnapshot, lerpSnapshotInto, rankReplayAngles } from '../director';
import { createGraphicsLayer, type GraphicsLayer } from '../graphics';
import { clockParts, minuteInHalf } from '../graphics/format';
import type { AudioEngine } from '../audio';
import { LETTER_COLORS } from '../ui/format';
import { goalCoverage } from '../meta/progress';
import { decodeFrame, frameIndexAt, type ClipFrame, type HighlightClip, type HighlightsData } from './recorder';
import { drawOverlays, type CardInfo, type OverlayView } from './compositor';

export interface ReelOptions {
  renderer: THREE.WebGLRenderer;
  data: HighlightsData;
  summary: ResultSummary;
  root: HTMLElement;
  audio?: AudioEngine | null;
  quality?: WorldQuality;
  pixelRatio?: number;
  /** Start straight into "Save video". */
  exportOnStart?: boolean;
  onClose(): void;
  onExported?(blob: Blob, info: { mimeType: string; seconds: number; filename: string }): void;
}

export interface HighlightsReel {
  readonly exporting: boolean;
  startExport(): void;
  resize(): void;
  stats(): { segment: number; segments: number; kind: string; elapsed: number; total: number; paused: boolean; exporting: boolean; ended: boolean; camId: string };
  skip(): void;
  dispose(): void;
}

const EXPORT_W = 1280, EXPORT_H = 720;
const STINGER_LEAD = 0.3;
const INTRO_S = 4.5, OUTRO_S = 6.5;

const TITLE: Partial<Record<MatchEventType, string>> = {
  goal: 'GOAL!', save: 'GREAT SAVE', near_miss: 'SO CLOSE', woodwork: 'OFF THE WOODWORK', yellow_card: 'YELLOW CARD',
  red_card: 'RED CARD', penalty: 'PENALTY', shot_on_target: 'ON TARGET', var_check: 'VAR CHECK', shot: 'CHANCE', counter_attack: 'COUNTER',
};

/** MediaRecorder + captureStream present, and some container is supported. */
export function canExportVideo(): boolean {
  return typeof MediaRecorder !== 'undefined' && typeof HTMLCanvasElement !== 'undefined'
    && typeof HTMLCanvasElement.prototype.captureStream === 'function' && pickMime() !== null;
}

function pickMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const opts = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4;codecs=avc1,mp4a', 'video/mp4'];
  for (const m of opts) { try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* ignore */ } }
  return null;
}

type Seg =
  | { kind: 'intro'; dur: number }
  | { kind: 'outro'; dur: number }
  | { kind: 'clip'; dur: number; clip: HighlightClip; from: number; to: number }
  | { kind: 'replay'; dur: number; clip: HighlightClip; ev: MatchEvent; camId: string; from: number; to: number };

/** Slow-mo ramp for the replay pass: real speed near the strike, 0.35× around it. */
const replaySpeed = (t: number, keyT: number): number => (Math.abs(t - keyT) <= 1.1 ? 0.35 : 1);

const h = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', html = ''): HTMLElementTagNameMap[K] => {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (html) el.innerHTML = html;
  return el;
};
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const mmss = (s: number) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.floor(Math.max(0, s) % 60)).padStart(2, '0')}`;

export function createHighlightsReel(o: ReelOptions): HighlightsReel {
  const { renderer, data, summary } = o;
  const { config, kit, clips } = data;
  const camLabel = (id: string) => kit.find((c) => c.id === id)?.label ?? id;

  // ------------------------------------------------------------------ 3D
  const world: World = createWorld(config);
  world.setQuality(o.quality ?? 'medium');
  const crew: CameraCrew = createCameraCrew(kit, { anchors: world.anchors, seed: config.seed });
  const n = clips[0]?.frames[0]?.roster.length ?? 22;
  const liveSnap = createEmptySnapshot(n), replaySnap = createEmptySnapshot(n);
  const scratchA = createEmptySnapshot(n), scratchB = createEmptySnapshot(n);
  let posed: FeedSource | null = null;
  let replayValid = false;
  const multiview: Multiview = createMultiview(renderer, world.scene, crew, {
    onBeforeFeed: (feed) => {
      const want: FeedSource = feed.source === 'replay' && replayValid ? 'replay' : 'live';
      if (want === posed) return;
      if (want === 'replay') world.update(replaySnap, replaySnap, 0, 0); else world.update(liveSnap, liveSnap, 0, 0);
      posed = want;
    },
  });

  const sampleInto = (frames: readonly ClipFrame[], t: number, out: WorldSnapshot): WorldSnapshot => {
    if (frames.length < 2) return decodeFrame(frames[0]!, out);
    const i = frameIndexAt(frames, t);
    const a = frames[i]!, b = frames[i + 1]!;
    decodeFrame(a, scratchA); decodeFrame(b, scratchB);
    const span = b.t - a.t;
    return lerpSnapshotInto(out, scratchA, scratchB, span > 0 ? Math.max(0, Math.min(1, (t - a.t) / span)) : 0);
  };

  /** Replay playhead that was on air at sim time t (NaN if the program was live). */
  const headAt = (frames: readonly ClipFrame[], t: number): number => {
    const i = frameIndexAt(frames, t);
    const a = frames[i]!, b = frames[i + 1] ?? a;
    const fa = Number.isFinite(a.airHead), fb = Number.isFinite(b.airHead);
    if (fa && fb) { const k = b.t > a.t ? Math.max(0, Math.min(1, (t - a.t) / (b.t - a.t))) : 0; return a.airHead + (b.airHead - a.airHead) * k; }
    if (fa) return a.airHead;
    if (fb) return b.airHead;
    for (let j = i - 1; j >= Math.max(0, i - 12); j--) if (Number.isFinite(frames[j]!.airHead)) return frames[j]!.airHead;
    return NaN;
  };

  /** Which camera/source was on PGM at sim time t (from the player's own cut log). */
  const airAt = (cuts: readonly CutRecord[], t: number): { camId: string; source: FeedSource; blend?: FeedLayout['blend'] } => {
    let i = 0;
    for (let k = 1; k < cuts.length; k++) if (cuts[k]!.t <= t) i = k; else break;
    const cur = cuts[i]!, prev = i > 0 ? cuts[i - 1]! : null;
    if (prev && cur.transition === 'stinger' && t - cur.t < STINGER_LEAD) return { camId: prev.camId, source: prev.isReplay ? 'replay' : 'live' };
    const out: { camId: string; source: FeedSource; blend?: FeedLayout['blend'] } = { camId: cur.camId, source: cur.isReplay ? 'replay' : 'live' };
    if (prev && cur.transition === 'mix' && t - cur.t < 0.5) out.blend = { camId: prev.camId, source: prev.isReplay ? 'replay' : 'live', weight: 1 - (t - cur.t) / 0.5 };
    return out;
  };

  // ------------------------------------------------------------------ timeline
  const segs: Seg[] = [{ kind: 'intro', dur: INTRO_S }];
  for (const clip of clips) {
    const p = clip.primary;
    const from = p.type === 'goal' ? clip.fromT : Math.max(clip.fromT, p.t - 5);
    segs.push({ kind: 'clip', clip, from, to: clip.toT, dur: Math.max(1, clip.toT - from) });
    if (p.type === 'goal') {
      const liveCam = airAt(clip.cuts, p.t + 0.3).camId;
      const ranked = rankReplayAngles(p, kit);
      const camId = ranked.find((id) => id !== liveCam) ?? ranked[0] ?? 'CAM 1';
      const rf = Math.max(clip.frames[0]!.t, p.t - 3.2), rt = Math.min(clip.frames[clip.frames.length - 1]!.t, p.t + 2);
      let d = 0;
      for (let t = rf; t < rt; d += 1 / 120) t += replaySpeed(t, p.t) / 120;
      if (rt - rf > 1) segs.push({ kind: 'replay', clip, ev: p, camId, from: rf, to: rt, dur: d });
    }
  }
  segs.push({ kind: 'outro', dur: OUTRO_S });
  const total = segs.reduce((s, x) => s + x.dur, 0);
  const segStart = segs.map((_, i) => segs.slice(0, i).reduce((s, x) => s + x.dur, 0));

  // Red cards for the scorebug, from every recorded event.
  const allEvents = clips.flatMap((c) => c.events);
  const zero = (): TeamMatchStats => ({ possessionPct: 50, shots: 0, shotsOnTarget: 0, corners: 0, fouls: 0, yellow: 0, red: 0, passes: 0, passAccuracyPct: 0, xg: 0 });
  const stats: MatchStats = { home: zero(), away: zero(), scorers: [], players: {} };

  const goals = goalCoverage(summary);
  const card: CardInfo = {
    competition: config.competition, venue: config.venue,
    homeName: config.home.name, awayName: config.away.name, homeShort: config.home.shortName, awayShort: config.away.shortName,
    homeColor: config.home.primaryColor, awayColor: config.away.primaryColor,
    score: { ...summary.score }, letter: summary.report.letter, letterColor: LETTER_COLORS[summary.report.letter],
    rating: summary.report.rating,
    goalsLine: goals.goals ? `${goals.covered}/${goals.goals} GOALS ON AIR` : `${clips.length} MOMENTS`,
    clipCount: clips.length,
  };

  // ------------------------------------------------------------------ DOM
  const el = h('div', 'sdh');
  el.dataset.ui = 'highlights';
  const frame = h('div', 'sdh-frame');
  const layer = h('div', 'sdh-layer');
  const lt = h('div', 'sdh-lt', '<b class="sdh-lt__min"></b><div class="sdh-lt__body"><b class="sdh-lt__title"></b><span class="sdh-lt__text"></span></div>');
  const stamp = h('div', 'sdh-stamp', '<b></b><span></span>');
  const tag = h('div', 'sdh-tag');
  const intro = h('div', 'sdh-card sdh-card--intro', `
    <i class="sdh-wedge sdh-wedge--h"></i><i class="sdh-wedge sdh-wedge--a"></i>
    <div class="sdh-card__in">
      <div class="sdh-card__kicker">MATCH HIGHLIGHTS</div>
      <div class="sdh-card__comp">${esc(config.competition.toUpperCase())}</div>
      <div class="sdh-card__teams"><b>${esc(config.home.name.toUpperCase())}</b><span class="sdh-card__score">${summary.score.home}–${summary.score.away}</span><b>${esc(config.away.name.toUpperCase())}</b></div>
      <div class="sdh-card__ft">FULL TIME</div>
      <div class="sdh-card__meta"><span>${esc(config.venue.toUpperCase())} · ${clips.length} MOMENTS · YOUR GRADE</span><b class="sdh-card__chip">${card.letter}</b></div>
    </div>`);
  const outro = h('div', 'sdh-card sdh-card--outro', `
    <i class="sdh-wedge sdh-wedge--h"></i><i class="sdh-wedge sdh-wedge--a"></i>
    <div class="sdh-card__in">
      <div class="sdh-card__kicker">DIRECTOR RATING</div>
      <div class="sdh-card__letter">${card.letter}</div>
      <div class="sdh-card__line">${Math.round(card.rating)} / 100 · ${esc(card.goalsLine)}</div>
      <div class="sdh-card__sub">${esc(config.home.name.toUpperCase())} ${summary.score.home}–${summary.score.away} ${esc(config.away.name.toUpperCase())}</div>
      <div class="sdh-card__by">DIRECTED BY YOU</div>
      <div class="sdh-card__brand">SPORTS DIRECTOR</div>
    </div>`);
  frame.style.setProperty('--home', card.homeColor);
  frame.style.setProperty('--away', card.awayColor);
  frame.style.setProperty('--lc', card.letterColor);
  layer.append(lt, stamp, tag, intro, outro);
  frame.append(layer);
  const gfx: GraphicsLayer = createGraphicsLayer(frame, config, { autoStinger: false }); // appended after → stinger on top

  const progress = h('div', 'sdh-progress');
  const ticks = segs.map((s, i) => {
    const t = h('i', `sdh-tick sdh-tick--${s.kind}`);
    t.style.left = `${(segStart[i]! / total) * 100}%`;
    t.style.width = `${(s.dur / total) * 100}%`;
    progress.append(t);
    return t;
  });
  const fill = h('b', 'sdh-progress__fill');
  progress.append(fill);
  const timeEl = h('span', 'sdh-time');
  const btn = (label: string, key: string, cls = '') => {
    const b = h('button', `sd-btn sd-btn--small ${cls}`, `<span>${label}</span><kbd>${key}</kbd>`);
    b.type = 'button';
    return b;
  };
  const pauseBtn = btn('PAUSE', 'SPACE');
  const skipBtn = btn('SKIP', '→');
  const saveBtn = btn('SAVE VIDEO', 'S');
  const closeBtn = btn('CLOSE', 'ESC', 'sd-btn--ghost');
  if (!canExportVideo()) saveBtn.hidden = true;
  const bar = h('div', 'sdh-bar');
  const left = h('div', 'sdh-bar__left', '<b class="sdh-bar__brand"><i></i>HIGHLIGHTS</b>');
  left.append(timeEl);
  const right = h('div', 'sdh-bar__right');
  right.append(pauseBtn, skipBtn, saveBtn, closeBtn);
  bar.append(progress, left, right);
  const rec = h('div', 'sdh-rec', '<i></i><b>REC</b><span class="sdh-rec__msg">SAVING VIDEO</span><span class="sdh-rec__pct">0%</span>');
  const toast = h('div', 'sdh-toast');
  el.append(frame, bar, rec, toast);
  o.root.append(el);

  // ------------------------------------------------------------------ state
  let segIdx = 0, segT = 0, segSim = 0, lastSim = 0;
  let rt = 0;
  let paused = false, ended = false, disposed = false;
  let stingerAt = -10, stingerFired = false, skipAt = -1;
  let lastSrc: FeedSource = 'live';
  let curCam = 'CAM 1';
  let raf = 0, lastNow = 0;
  let idleTimer = 0;

  const scorebugInst: GraphicInstance = { id: 1, kind: 'scorebug', shownAt: 0 };
  const replayState: ReplayState = { active: false, fromT: 0, toT: 0, headT: 0, speed: 1, camId: 'CAM 1', eventId: null };
  const dirState: DirectorState = { programCamId: 'CAM 1', previewCamId: 'CAM 2', cuts: [], replay: replayState, graphics: [], graphicsLog: [], instructions: {} };

  const fireStinger = () => {
    stingerAt = rt;
    gfx.playStinger();
    o.audio?.ui('whoosh');
  };

  const clipSnap0 = (i: number): ClipFrame | null => {
    const s = segs[i];
    if (s && (s.kind === 'clip' || s.kind === 'replay')) return s.clip.frames[0]!;
    const c = i === 0 ? clips[0] : clips[clips.length - 1];
    if (!c) return null;
    return i === 0 ? c.frames[frameIndexAt(c.frames, c.fromT)]! : c.frames[c.frames.length - 1]!;
  };

  function enterSeg(i: number): void {
    segIdx = Math.max(0, Math.min(segs.length - 1, i));
    const s = segs[segIdx]!;
    segT = 0; stingerFired = false; skipAt = -1;
    crew.endReplay();
    crew.reset();
    lastSrc = 'live';
    if (s.kind === 'clip') {
      segSim = s.from; lastSim = s.from;
      // settle the operators on the pre-roll they'd have been watching
      const f0 = Math.max(s.clip.frames[0]!.t, s.from - 1.5);
      for (let t = f0; t <= s.from; t += 1 / 15) { sampleInto(s.clip.frames, t, liveSnap); crew.update(liveSnap, 1 / 15, []); }
    } else if (s.kind === 'replay') {
      segSim = s.from; lastSim = s.from;
      sampleInto(s.clip.frames, s.from, liveSnap);
      crew.update(liveSnap, 1 / 30, []);
    } else {
      const f = clipSnap0(segIdx);
      if (f) decodeFrame(f, liveSnap);
      crew.update(liveSnap, 1 / 30, []);
    }
    for (let k = 0; k < ticks.length; k++) ticks[k]!.classList.toggle('is-done', k < segIdx);
    if (s.kind === 'outro') o.audio?.ui('chime');
  }

  // ------------------------------------------------------------------ export
  let exporting = false;
  let mr: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let out2d: CanvasRenderingContext2D | null = null;
  let outCanvas: HTMLCanvasElement | null = null;
  let cancelled = false;
  let exportStart = 0;

  const showToast = (msg: string) => {
    toast.textContent = msg;
    toast.classList.remove('is-on'); void toast.offsetWidth; toast.classList.add('is-on');
  };

  const restoreRenderer = () => {
    renderer.setPixelRatio(o.pixelRatio ?? Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
  };

  async function startExport(): Promise<void> {
    if (exporting || disposed) return;
    const mime = pickMime();
    if (!mime) { showToast('VIDEO EXPORT NOT SUPPORTED IN THIS BROWSER'); return; }
    exporting = true;
    cancelled = false;
    el.classList.add('is-exporting');
    document.documentElement.classList.add('sd-reel-export');
    try { await Promise.race([document.fonts.load(`italic 800 40px "Barlow Condensed"`), new Promise((r) => setTimeout(r, 1500))]); } catch { /* fonts optional */ }
    if (disposed) return;
    outCanvas = document.createElement('canvas');
    outCanvas.width = EXPORT_W; outCanvas.height = EXPORT_H;
    out2d = outCanvas.getContext('2d');
    renderer.setPixelRatio(1);
    renderer.setSize(EXPORT_W, EXPORT_H, false);
    const stream = outCanvas.captureStream(30);
    const at = o.audio?.ready ? o.audio.captureStream()?.getAudioTracks()[0] : undefined;
    const useMime = at ? mime : mime.replace(/,opus|,mp4a/, '');
    if (at) stream.addTrack(at);
    chunks = [];
    mr = new MediaRecorder(stream, MediaRecorder.isTypeSupported(useMime) ? { mimeType: useMime, videoBitsPerSecond: 8_000_000 } : { videoBitsPerSecond: 8_000_000 });
    const recMime = mr.mimeType || useMime;
    mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    mr.onstop = () => {
      stream.getVideoTracks().forEach((t) => t.stop());
      const seconds = (performance.now() - exportStart) / 1000;
      const wasCancelled = cancelled;
      endExportUi();
      if (wasCancelled) return;
      const blob = new Blob(chunks, { type: recMime.split(';')[0] });
      chunks = [];
      const ext = /mp4/.test(recMime) ? 'mp4' : 'webm';
      const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const filename = `sports-director-${slug(config.home.shortName)}-${summary.score.home}-${summary.score.away}-${slug(config.away.shortName)}.${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.style.display = 'none';
      document.body.append(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      showToast(`VIDEO SAVED · ${(blob.size / 1e6).toFixed(1)} MB`);
      o.onExported?.(blob, { mimeType: recMime, seconds, filename });
    };
    paused = false; ended = false;
    enterSeg(0);
    exportStart = performance.now();
    mr.start(500);
  }

  function endExportUi(): void {
    exporting = false;
    mr = null; out2d = null; outCanvas = null;
    el.classList.remove('is-exporting');
    document.documentElement.classList.remove('sd-reel-export');
    if (!disposed) restoreRenderer();
  }

  function stopExport(cancel: boolean): void {
    if (!exporting) return;
    cancelled = cancel;
    if (mr && mr.state !== 'inactive') mr.stop(); else endExportUi();
  }

  // ------------------------------------------------------------------ per-frame
  const view: OverlayView = { scorebug: null, lowerThird: null, stamp: null, replay: null, card: null, stingerAge: -1, tag: '' };

  function computeAndRender(dt: number): void {
    const s = segs[segIdx]!;
    view.scorebug = null; view.lowerThird = null; view.stamp = null; view.replay = null; view.card = null;
    let layout: FeedLayout;
    const rect = exporting ? { x: 0, y: 0, w: EXPORT_W, h: EXPORT_H } : { x: 0, y: 0, w: window.innerWidth, h: window.innerHeight };
    let newEvents: MatchEvent[] = [];
    replayValid = false;
    let replaySpeedNow = 1;

    if (s.kind === 'clip') {
      const clip = s.clip;
      const simT = Math.min(s.to, s.from + segT);
      segSim = simT;
      newEvents = clip.events.filter((e) => e.t > lastSim && e.t <= simT);
      for (let k = 1; k < clip.cuts.length; k++) {
        const c = clip.cuts[k]!;
        if (c.transition === 'stinger' && c.t > lastSim && c.t <= simT) fireStinger();
      }
      sampleInto(clip.frames, simT, liveSnap);
      crew.update(liveSnap, dt, newEvents);
      const air = airAt(clip.cuts, simT);
      if (air.source === 'replay' || air.blend?.source === 'replay') {
        const head = headAt(clip.frames, simT);
        if (Number.isFinite(head)) {
          sampleInto(clip.frames, head, replaySnap);
          replayValid = true;
          if (lastSrc !== 'replay') crew.endReplay();
          const evId = clip.frames[frameIndexAt(clip.frames, simT)]!.replayEventId;
          const ev = evId !== null ? clip.events.find((e) => e.id === evId) ?? null : null;
          crew.replayCamera(air.source === 'replay' ? air.camId : air.blend!.camId, replaySnap, { event: ev });
          const ahead = headAt(clip.frames, simT + 0.2);
          if (Number.isFinite(ahead)) replaySpeedNow = Math.max(0.1, (ahead - head) / 0.2);
        }
      }
      if (air.source === 'replay' && !replayValid) air.source = 'live';
      lastSrc = air.source;
      curCam = air.camId;
      layout = { camId: air.camId, rect, tier: 'program', source: air.source };
      if (air.blend) layout.blend = air.blend;
      lastSim = simT;

      const p = clip.primary;
      const fAt = clip.frames[frameIndexAt(clip.frames, p.t)]!;
      // Goals: caption after the ball goes in (don't spoil it); everything else: up front.
      const ltStart = p.type === 'goal' ? Math.min(p.t - s.from + 1.2, s.dur - 2.5) : 0.6;
      const ltEnd = Math.min(ltStart + 5, s.dur - 0.4);
      if (segT >= ltStart && segT < ltEnd) {
        const accent = p.team === 'home' ? card.homeColor : p.team === 'away' ? card.awayColor : '#5b3cff';
        const teamName = p.team === 'home' ? config.home.name : p.team === 'away' ? config.away.name : '';
        view.lowerThird = {
          minute: minuteInHalf(p.matchClock, fAt.half), title: `${TITLE[p.type] ?? p.type.replace(/_/g, ' ').toUpperCase()}${teamName ? ` · ${teamName.toUpperCase()}` : ''}`,
          text: p.text, accent, age: segT - ltStart, left: ltEnd - segT,
        };
      }
      const vAt = p.t - s.from + 0.5;
      if (clip.verdict && segT >= vAt && segT < Math.min(vAt + 3.2, s.dur)) {
        const sev = clip.verdict.severity;
        const onAir = airAt(clip.cuts, p.t);
        view.stamp = {
          label: clip.verdict.label.replace(/!$/, '') + (sev === 'great' || sev === 'terrible' ? '!' : ''),
          sub: `ON AIR: ${onAir.source === 'replay' ? 'A REPLAY' : camLabel(onAir.camId)}`,
          tone: sev === 'great' || sev === 'good' ? 'good' : sev === 'info' ? 'info' : 'bad', age: segT - vAt,
        };
      }
      if (air.source === 'replay') view.replay = { slow: replaySpeedNow < 0.9, label: `SLOW-MO ${replaySpeedNow < 0.3 ? replaySpeedNow.toFixed(2) : replaySpeedNow.toFixed(1)}×` };
      const sb = clockParts(liveSnap);
      view.scorebug = { sh: liveSnap.score.home, sa: liveSnap.score.away, clock: sb.main, period: sb.period, extra: sb.extra };
    } else if (s.kind === 'replay') {
      const sp = replaySpeed(segSim, s.ev.t);
      replaySpeedNow = sp;
      segSim = Math.min(s.to, segSim + dt * sp);
      sampleInto(s.clip.frames, segSim, replaySnap);
      copyInto(liveSnap, replaySnap);
      replayValid = true;
      crew.replayCamera(s.camId, replaySnap, { event: s.ev, dt: dt * sp });
      curCam = s.camId;
      layout = { camId: s.camId, rect, tier: 'program', source: 'replay' };
      view.replay = { slow: sp < 0.9, label: `SLOW-MO ${sp.toFixed(2).replace(/0$/, '')}×` };
      const end = s.dur - 0.5;
      if (segT >= 0.5 && segT < end) {
        view.lowerThird = { minute: minuteInHalf(s.ev.matchClock, replaySnap.half), title: 'THE BEST ANGLE', text: `${camLabel(s.camId)} · ${s.ev.text}`, accent: '#5b3cff', age: segT - 0.5, left: end - segT };
      }
      const sb = clockParts(replaySnap);
      view.scorebug = { sh: replaySnap.score.home, sa: replaySnap.score.away, clock: sb.main, period: sb.period, extra: sb.extra };
      lastSim = segSim;
    } else {
      crew.update(liveSnap, dt, []);
      curCam = 'CAM 1';
      layout = { camId: 'CAM 1', rect, tier: 'program', source: 'live' };
      view.card = { kind: s.kind, age: segT };
    }

    // red cards up to now (scorebug)
    let rh = 0, ra = 0;
    for (const e of allEvents) if (e.type === 'red_card' && e.t <= segSim) { if (e.team === 'home') rh++; else if (e.team === 'away') ra++; }
    stats.home.red = rh; stats.away.red = ra;

    view.stingerAge = rt - stingerAt <= 0.7 ? rt - stingerAt : -1;
    const clipNo = clips.indexOf((s as { clip?: HighlightClip }).clip as HighlightClip) + 1;
    view.tag = clipNo > 0 ? `HIGHLIGHTS · ${clipNo}/${clips.length}` : 'HIGHLIGHTS';

    // render
    world.update(liveSnap, liveSnap, 0, dt);
    posed = 'live';
    multiview.render([layout]);
    if (exporting && out2d) {
      out2d.drawImage(renderer.domElement, 0, 0, EXPORT_W, EXPORT_H);
      drawOverlays(out2d, EXPORT_W, EXPORT_H, card, view, rt);
    }

    // DOM overlays (on-screen)
    applyDom(s, newEvents, replaySpeedNow);
    if (o.audio && !paused) o.audio.update(s.kind === 'replay' ? replaySnap : liveSnap, newEvents, dt);
  }

  const copyInto = (dst: WorldSnapshot, src: WorldSnapshot) => lerpSnapshotInto(dst, src, src, 0);

  let ltKey = '', stampKey = '';
  const ltMin = lt.querySelector<HTMLElement>('.sdh-lt__min')!, ltTitle = lt.querySelector<HTMLElement>('.sdh-lt__title')!, ltText = lt.querySelector<HTMLElement>('.sdh-lt__text')!;
  const stLabel = stamp.querySelector<HTMLElement>('b')!, stSub = stamp.querySelector<HTMLElement>('span')!;

  function applyDom(s: Seg, _ev: MatchEvent[], speed: number): void {
    // scorebug + replay bug via the broadcast graphics layer
    dirState.graphics = view.scorebug ? [scorebugInst] : [];
    replayState.active = !!view.replay;
    replayState.speed = speed;
    gfx.sync(dirState, s.kind === 'replay' ? replaySnap : liveSnap, stats, replayState);

    const lk = view.lowerThird ? `${segIdx}|${view.lowerThird.title}` : '';
    if (lk !== ltKey) {
      ltKey = lk;
      if (view.lowerThird) {
        ltMin.textContent = view.lowerThird.minute; ltTitle.textContent = view.lowerThird.title; ltText.textContent = view.lowerThird.text;
        lt.style.setProperty('--accent', view.lowerThird.accent);
        lt.classList.remove('is-on'); void lt.offsetWidth; lt.classList.add('is-on');
      } else lt.classList.remove('is-on');
    }
    const sk = view.stamp ? `${segIdx}|${view.stamp.label}` : '';
    if (sk !== stampKey) {
      stampKey = sk;
      if (view.stamp) {
        stLabel.textContent = view.stamp.label; stSub.textContent = view.stamp.sub;
        stamp.dataset.tone = view.stamp.tone;
        stamp.classList.remove('is-on'); void stamp.offsetWidth; stamp.classList.add('is-on');
      } else stamp.classList.remove('is-on');
    }
    tag.textContent = view.tag;
    tag.classList.toggle('is-on', !view.card && !view.replay);
    intro.classList.toggle('is-on', view.card?.kind === 'intro');
    outro.classList.toggle('is-on', view.card?.kind === 'outro');

    const elapsed = segStart[segIdx]! + segT;
    fill.style.width = `${(Math.min(total, elapsed) / total) * 100}%`;
    timeEl.textContent = `${mmss(elapsed)} / ${mmss(total)}`;
    if (exporting) rec.querySelector<HTMLElement>('.sdh-rec__pct')!.textContent = `${Math.round((Math.min(total, elapsed) / total) * 100)}%`;
  }

  function tick(now: number): void {
    if (disposed) return;
    raf = requestAnimationFrame(tick);
    const dt = lastNow ? Math.min(0.1, Math.max(0, (now - lastNow) / 1000)) : 0;
    lastNow = now;
    if (paused) { if (exporting) computeAndRender(0); return; }
    rt += dt;
    const s = segs[segIdx]!;
    if (!ended) {
      segT += dt;
      if (skipAt >= 0 && rt >= skipAt) { enterSeg(segIdx + 1); }
      else if (segIdx < segs.length - 1) {
        if (!stingerFired && s.dur - segT <= STINGER_LEAD) { stingerFired = true; fireStinger(); }
        if (segT >= s.dur) enterSeg(segIdx + 1);
      } else if (segT >= s.dur) {
        segT = s.dur;
        if (exporting) stopExport(false);
        else { ended = true; el.classList.add('is-ended'); pauseBtn.firstElementChild!.textContent = 'REPLAY'; }
      }
    }
    computeAndRender(dt);
  }

  // ------------------------------------------------------------------ controls
  function setPaused(p: boolean): void {
    if (ended && !p) { ended = false; el.classList.remove('is-ended'); enterSeg(0); }
    paused = p;
    el.classList.toggle('is-paused', p);
    pauseBtn.firstElementChild!.textContent = p ? 'PLAY' : 'PAUSE';
    if (mr && exporting) { if (p && mr.state === 'recording') mr.pause(); else if (!p && mr.state === 'paused') mr.resume(); }
    o.audio?.setPaused(p);
  }
  function skip(): void {
    if (ended || skipAt >= 0) return;
    if (segIdx >= segs.length - 1) { segT = segs[segIdx]!.dur; return; }
    if (paused) setPaused(false);
    fireStinger();
    skipAt = rt + STINGER_LEAD;
    stingerFired = true;
  }
  function close(): void {
    if (exporting) stopExport(true);
    o.onClose();
  }

  pauseBtn.addEventListener('click', () => (ended ? setPaused(false) : setPaused(!paused)));
  skipBtn.addEventListener('click', skip);
  saveBtn.addEventListener('click', () => { void startExport(); });
  closeBtn.addEventListener('click', close);
  const onKey = (e: KeyboardEvent) => {
    if (disposed) return;
    let handled = true;
    if (e.key === ' ' || e.code === 'Space') { if (ended) setPaused(false); else setPaused(!paused); }
    else if (e.key === 'ArrowRight') skip();
    else if (e.key === 'Escape') close();
    else if ((e.key === 's' || e.key === 'S') && !saveBtn.hidden) void startExport();
    else handled = false;
    if (handled) { e.preventDefault(); e.stopImmediatePropagation(); }
  };
  window.addEventListener('keydown', onKey, true);
  const wake = () => {
    el.classList.add('is-awake');
    window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => el.classList.remove('is-awake'), 2600);
  };
  el.addEventListener('pointermove', wake);
  wake();

  // ------------------------------------------------------------------ start
  renderer.setPixelRatio(o.pixelRatio ?? Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  o.audio?.setPaused(false);
  enterSeg(0);
  raf = requestAnimationFrame(tick);
  if (o.exportOnStart) void startExport();

  return {
    get exporting() { return exporting; },
    startExport() { void startExport(); },
    resize() { if (!exporting) renderer.setSize(window.innerWidth, window.innerHeight); },
    skip,
    stats() {
      return { segment: segIdx, segments: segs.length, kind: segs[segIdx]!.kind, elapsed: Math.round((segStart[segIdx]! + segT) * 10) / 10, total: Math.round(total * 10) / 10, paused, exporting, ended, camId: curCam };
    },
    dispose() {
      if (disposed) return;
      if (exporting) stopExport(true);
      disposed = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(idleTimer);
      window.removeEventListener('keydown', onKey, true);
      document.documentElement.classList.remove('sd-reel-export');
      o.audio?.setPaused(true);
      gfx.dispose();
      el.remove();
      multiview.dispose();
      world.dispose();
      renderer.renderLists.dispose();
      restoreRenderer();
      renderer.setRenderTarget(null);
      renderer.setScissorTest(false);
      renderer.clear(true, true, true);
    },
  };
}
